import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { TASK_PRIORITIES, TASK_STATUSES, priorityLabel, statusLabel } from '../constants';
import { fromDateInput } from '../format';
import type { Task, TaskCreateBody, TaskResponse, UserWithRole } from '../types';
import { Modal } from './Modal';

export interface TaskCreateDialogProps {
  users: UserWithRole[];
  canAssign: boolean;
  onClose: () => void;
  onCreated: (task: Task) => void;
}

/** Formular für eine neue Aufgabe. */
export function TaskCreateDialog({
  users,
  canAssign,
  onClose,
  onCreated,
}: TaskCreateDialogProps): ReactNode {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('normal');
  const [status, setStatus] = useState<string>('open');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    if (title.trim() === '') {
      setError('Ein Titel ist erforderlich.');
      return;
    }
    setBusy(true);
    setError(null);

    const body: TaskCreateBody = {
      title: title.trim(),
      description: description.trim(),
      priority,
      status,
    };
    if (canAssign && assigneeId !== '') body.assigneeId = assigneeId;
    const dueAt = fromDateInput(dueDate);
    if (dueAt !== null) body.dueAt = dueAt;

    try {
      const result = await api.post<TaskResponse>('/api/tasks', body);
      onCreated(result.task);
    } catch (cause: unknown) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  };

  return (
    <Modal title="Neue Aufgabe" onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        <label className="field">
          <span>Titel *</span>
          <input
            type="text"
            value={title}
            maxLength={200}
            autoFocus
            placeholder="Was ist zu tun?"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Beschreibung</span>
          <textarea
            rows={4}
            value={description}
            maxLength={5000}
            placeholder="Details, Links, Kontext …"
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <div className="form-row">
          <label className="field">
            <span>Priorität</span>
            <select value={priority} onChange={(event) => setPriority(event.target.value)}>
              {TASK_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {priorityLabel(value)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              {TASK_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-row">
          {canAssign && (
            <label className="field">
              <span>Zuweisen an</span>
              <select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
                <option value="">Niemand</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} (@{user.username})
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            <span>Fällig am</span>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </label>
        </div>

        {!canAssign && (
          <p className="muted small">
            Zuweisungen benötigen das Recht <code>task.assign</code>.
          </p>
        )}

        {error !== null && (
          <div className="feedback feedback-error" role="alert">
            <span className="feedback-icon" aria-hidden="true">
              !
            </span>
            <span>{error}</span>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="button button-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? 'Anlegen …' : 'Aufgabe anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
